import React, { useState, useEffect, useCallback, useRef } from "react";
import { Form, Spinner, Badge } from "react-bootstrap";
import {
  FaCode, FaSearch, FaHistory, FaCheckCircle, FaAdjust,
  FaDotCircle, FaRegCircle, FaSortAmountDown, FaTimes,
  FaChevronLeft, FaChevronRight, FaJava, FaJs, FaPython, FaFilter
} from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import PracticeQuestionCard from "./PracticeQuestionCard";
import PracticeStatsBar from "./PracticeStatsBar";
import "./practice.scss";

const PER_PAGE = 20;

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "a-z", label: "Title A → Z" },
  { value: "z-a", label: "Title Z → A" },
  { value: "easy-first", label: "Easy → Hard" },
  { value: "hard-first", label: "Hard → Easy" },
];

const STATUS_TABS = [
  { key: "", label: "All", icon: null },
  { key: "not_attempted", label: "Not Attempted", icon: <FaRegCircle size={10} /> },
  { key: "attempted", label: "Attempted", icon: <FaDotCircle size={10} className="text-info" /> },
  { key: "partially_solved", label: "Partial", icon: <FaAdjust size={10} className="text-warning" /> },
  { key: "solved", label: "Solved", icon: <FaCheckCircle size={10} className="text-success" /> },
];

const TECH_ICON_MAP = {
  Java: <FaJava />,
  JavaScript: <FaJs />,
  Python: <FaPython />,
};

const PracticeQuestionsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = getCurrentUser();
  const studentId = currentUser?.studentId;
  const searchTimerRef = useRef(null);

  // Helper to get saved filter or default
  const getSavedFilter = (key, defaultVal) => {
    const urlVal = searchParams.get(key);
    if (urlVal !== null) return urlVal;
    try {
      return localStorage.getItem(`pq_${key}`) || defaultVal;
    } catch {
      return defaultVal;
    }
  };

  // Restore filters from URL or localStorage
  const initialDifficulty = getSavedFilter("difficulty", "");
  const initialCategory = getSavedFilter("category", "");
  const initialStatus = getSavedFilter("status", "");
  const initialSort = getSavedFilter("sort", "newest");
  const initialPage = parseInt(searchParams.get("page")) || 1;
  const initialSearch = getSavedFilter("q", "");

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [difficulty, setDifficulty] = useState(initialDifficulty);
  const [category, setCategory] = useState(initialCategory);
  const [status, setStatus] = useState(initialStatus);
  const [sort, setSort] = useState(initialSort);
  const [page, setPage] = useState(initialPage);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filter metadata from backend
  const [filterMeta, setFilterMeta] = useState(null);

  // Debounced search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(searchTimerRef.current);
  }, [search]);

  // Fetch filter metadata (categories, difficulty counts)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/practice/filters");
        setFilterMeta(data);
      } catch { /* silent */ }
    })();
  }, []);

  // Sync filters to URL and localStorage
  useEffect(() => {
    const params = {};
    if (debouncedSearch) params.q = debouncedSearch;
    if (difficulty) params.difficulty = difficulty;
    if (category) params.category = category;
    if (status) params.status = status;
    if (sort !== "newest") params.sort = sort;
    if (page > 1) params.page = page;
    setSearchParams(params, { replace: true });

    // Save to localStorage
    try {
      localStorage.setItem("pq_q", debouncedSearch);
      localStorage.setItem("pq_difficulty", difficulty);
      localStorage.setItem("pq_category", category);
      localStorage.setItem("pq_status", status);
      localStorage.setItem("pq_sort", sort);
    } catch { /* ignore */ }
  }, [debouncedSearch, difficulty, category, status, sort, page, setSearchParams]);

  // Fetch questions
  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PER_PAGE, sort };
      if (studentId) params.studentId = studentId;
      if (difficulty) params.difficulty = difficulty;
      if (category) params.category = category;
      if (status) params.status = status;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

      const { data } = await apiClient.get("/practice/questions", { params });
      setQuestions(data.questions || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch {
      setQuestions([]);
      setTotal(0);
    }
    setLoading(false);
  }, [page, difficulty, category, status, sort, debouncedSearch, studentId]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [difficulty, category, status, sort, debouncedSearch]);

  // Active filter count
  const activeFilterCount = [difficulty, category, status].filter(Boolean).length +
    (sort !== "newest" ? 1 : 0) + (debouncedSearch ? 1 : 0);

  const clearAllFilters = () => {
    setSearch(""); setDebouncedSearch(""); setDifficulty("");
    setCategory(""); setStatus(""); setSort("newest"); setPage(1);
  };

  // Pagination helpers
  const getPaginationRange = () => {
    const delta = 2;
    const range = [];
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);
    range.push(1);
    if (left > 2) range.push("...");
    for (let i = left; i <= right; i++) range.push(i);
    if (right < totalPages - 1) range.push("...");
    if (totalPages > 1) range.push(totalPages);
    return range;
  };

  return (
    <div className="pq-page">
      {/* ── Header ── */}
      <div className="pq-header">
        <div className="pq-header-left">
          <h4 className="pq-title">
            <FaCode className="pq-title-icon" />
            Coding Practice
          </h4>
          <span className="pq-subtitle">
            {filterMeta ? `${filterMeta.totalQuestions} questions` : ""} · Practice at your own pace
          </span>
        </div>
        <button className="pq-history-btn" onClick={() => navigate("/s/practice/history")}>
          <FaHistory size={12} /> History
        </button>
      </div>

      {/* ── Stats ── */}
      <PracticeStatsBar studentId={studentId} />

      {/* ── Filter Section ── */}
      <div className="pq-filters-section">
        {/* Row 1: Search + Sort */}
        <div className="pq-filters-row-1">
          <div className="pq-search-wrap">
            <FaSearch className="pq-search-icon" />
            <input
              type="text"
              placeholder="Search by title or keyword…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pq-search-input"
            />
            {search && (
              <button className="pq-search-clear" onClick={() => { setSearch(""); setDebouncedSearch(""); }}>
                <FaTimes size={10} />
              </button>
            )}
          </div>
          {/* Technology dropdown */}
          {filterMeta?.categories?.length > 0 && (
            <div className="pq-sort-wrap">
              <Form.Select
                size="sm"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="pq-sort-select"
              >
                <option value="">All Technologies</option>
                {filterMeta.categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </Form.Select>
            </div>
          )}

          <div className="pq-sort-wrap">
            <Form.Select
              size="sm"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="pq-sort-select"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Form.Select>
          </div>
        </div>

        {/* Row 2: Difficulty pills */}
        <div className="pq-filters-row-2">
          <div className="pq-pill-group">
            <span className="pq-pill-label">Difficulty</span>
            {["", "Easy", "Medium", "Hard"].map((d) => (
              <button
                key={d || "all"}
                className={`pq-pill diff ${d.toLowerCase() || "all"}${difficulty === d ? " active" : ""}`}
                onClick={() => setDifficulty(d)}
              >
                {d || "All"}
              </button>
            ))}
          </div>
        </div>

        {/* Row 3: Status tabs */}
        <div className="pq-status-tabs">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              className={`pq-status-tab${status === t.key ? " active" : ""}`}
              onClick={() => setStatus(t.key)}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Active Filters Summary ── */}
      {activeFilterCount > 0 && (
        <div className="pq-active-filters">
          <FaFilter size={10} className="text-muted" />
          <span className="pq-af-label">{activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""} applied</span>
          {debouncedSearch && (
            <Badge bg="light" text="dark" className="pq-af-chip" onClick={() => { setSearch(""); setDebouncedSearch(""); }}>
              "{debouncedSearch}" <FaTimes size={8} />
            </Badge>
          )}
          {difficulty && (
            <Badge bg="light" text="dark" className="pq-af-chip" onClick={() => setDifficulty("")}>
              {difficulty} <FaTimes size={8} />
            </Badge>
          )}
          {category && (
            <Badge bg="light" text="dark" className="pq-af-chip" onClick={() => setCategory("")}>
              {category} <FaTimes size={8} />
            </Badge>
          )}
          {status && (
            <Badge bg="light" text="dark" className="pq-af-chip" onClick={() => setStatus("")}>
              {STATUS_TABS.find(s => s.key === status)?.label} <FaTimes size={8} />
            </Badge>
          )}
          {sort !== "newest" && (
            <Badge bg="light" text="dark" className="pq-af-chip" onClick={() => setSort("newest")}>
              {SORT_OPTIONS.find(s => s.value === sort)?.label} <FaTimes size={8} />
            </Badge>
          )}
          <button className="pq-clear-all" onClick={clearAllFilters}>Clear all</button>
        </div>
      )}

      {/* ── Results info ── */}
      {!loading && (
        <div className="pq-results-info">
          <span>
            Showing <strong>{Math.min((page - 1) * PER_PAGE + 1, total)}</strong>–<strong>{Math.min(page * PER_PAGE, total)}</strong> of <strong>{total}</strong> question{total !== 1 ? "s" : ""}
          </span>
        </div>
      )}

      {/* ── Question List ── */}
      {loading ? (
        <div className="pq-loading">
          <Spinner animation="border" size="sm" /> <span>Loading questions…</span>
        </div>
      ) : questions.length === 0 ? (
        <div className="pq-empty">
          <FaCode size={28} />
          <span className="pq-empty-title">No questions found</span>
          <span className="pq-empty-desc">
            {activeFilterCount > 0
              ? "Try adjusting your filters or search query"
              : "No practice questions are available yet"}
          </span>
          {activeFilterCount > 0 && (
            <button className="pq-empty-clear" onClick={clearAllFilters}>Clear all filters</button>
          )}
        </div>
      ) : (
        <>
          <div className="pq-list">
            {questions.map((q, idx) => (
              <PracticeQuestionCard
                key={q._id}
                question={q}
                index={(page - 1) * PER_PAGE + idx + 1}
              />
            ))}
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div className="pq-pagination">
              <button
                className="pq-page-btn nav"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <FaChevronLeft size={10} /> Prev
              </button>
              <div className="pq-page-numbers">
                {getPaginationRange().map((p, i) =>
                  p === "..." ? (
                    <span key={`dots-${i}`} className="pq-page-dots">…</span>
                  ) : (
                    <button
                      key={p}
                      className={`pq-page-btn ${page === p ? "active" : ""}`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  )
                )}
              </div>
              <button
                className="pq-page-btn nav"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next <FaChevronRight size={10} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PracticeQuestionsPage;
